import { useContext, useMemo } from 'react'
import { Check } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useSeasonTrinkets,
  useTeamAuditData,
} from 'app/frontend/base/config/queries'
import {
  TrinketChoice,
  updateTrinketPriority,
} from 'app/frontend/base/api/character'
import { displayErrorToast, wowheadUrl } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { LootContext } from '../context'
import { seasonWishlistMetadata, withSeasonWishlistMetadata } from '../helpers'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import { cn } from '@/lib/utils'
import UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import TrinketChoiceCard, { TRINKET_CLEAR_VALUE } from './TrinketChoiceCard'
import {
  detectedTrinketTrack,
  isChoiceObtained,
  isTrinketAutoObtained,
  TrinketPriority as TrinketPriorityMap,
  trinketRankColumnLabels,
  trinketRanks,
} from './trinketPriorityHelpers'

type Props = {
  characterId: number
  disabled?: boolean
}

const TrinketPriority = ({ characterId, disabled }: Props) => {
  const { team } = useTeam()
  const { selectedSeason } = useContext(LootContext)
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: auditData } = useTeamAuditData(
    team.settings.trinketPriorityEnabled.value ? team.id : undefined,
  )

  const teamQueryKey = ['team', { path: team.path }]

  const member = team.members?.find(
    (m) => m.characterReference.id === characterId,
  )
  const character = member?.characterReference
  const storedPriority: TrinketPriorityMap = seasonWishlistMetadata(
    character?.wishlistMetadata,
    selectedSeason.id,
  ).trinket_priority

  const minimumTrack = team.settings.tierPriorityMinimumTrack
    .value as UpgradeTrack
  const trinketCache =
    auditData?.[characterId]?.trinket_cache?.[String(selectedSeason.id)]

  const { data: seasonTrinkets } = useSeasonTrinkets(team.id, selectedSeason.id)

  const trinketItems = useMemo(() => {
    if (!character) return []
    return (seasonTrinkets ?? [])
      .filter((item) => item.eligible_classes.includes(character.classId))
      .map((item) => ({
        id: item.id,
        name: item.name,
        quality: item.quality,
        encounter_name: item.source_name,
      }))
  }, [seasonTrinkets, character])

  const mutation = useMutation({
    mutationFn: (nextPriority: TrinketPriorityMap) =>
      updateTrinketPriority(
        team.id,
        characterId,
        selectedSeason.id,
        nextPriority,
      ),
    onMutate: async (nextPriority) => {
      await queryClient.cancelQueries({ queryKey: teamQueryKey })
      const previousTeam = queryClient.getQueryData<wowaudit.Team>(teamQueryKey)

      queryClient.setQueryData<wowaudit.Team>(teamQueryKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.map((m) =>
                m.characterReference.id === characterId
                  ? {
                      ...m,
                      characterReference: {
                        ...m.characterReference,
                        wishlistMetadata: withSeasonWishlistMetadata(
                          m.characterReference.wishlistMetadata,
                          selectedSeason.id,
                          { trinket_priority: nextPriority },
                        ),
                      },
                    }
                  : m,
              ),
            }
          : old,
      )

      return { previousTeam }
    },
    onError: (error, _nextPriority, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamQueryKey, context.previousTeam)
      }
      displayErrorToast(error, t)
    },
    onSuccess: (newTeam) => {
      queryClient.setQueryData(teamQueryKey, newTeam)
    },
  })

  const setRankChoice = (rank: string, value: TrinketChoice) => {
    if (disabled || mutation.isPending) return
    mutation.mutate({ ...storedPriority, [rank]: value })
  }

  if (
    !team.settings.trinketPriorityEnabled.value ||
    gameVersion !== 'live' ||
    !character ||
    trinketItems.length === 0
  ) {
    return null
  }

  const canEdit = !disabled

  return (
    <div className='w-full min-w-0 flex-1 sm:min-w-96'>
      <h4 className='flex items-center gap-2 mb-1'>Trinket priority</h4>
      <div className='flex flex-col gap-2 sm:flex-row'>
        {trinketRanks.map((rank) => {
          const choice = storedPriority[rank]
          const autoObtained = choice
            ? isTrinketAutoObtained(choice.item_id, trinketCache, minimumTrack)
            : false
          const effectiveObtained = isChoiceObtained(
            choice ?? null,
            trinketCache,
            minimumTrack,
          )
          const chosenItem = choice
            ? trinketItems.find((item) => item.id === choice.item_id)
            : undefined
          const chosenIdsElsewhere = trinketRanks
            .filter((other) => other !== rank)
            .map((other) => storedPriority[other]?.item_id)
            .filter((id): id is number => !!id)

          const trinketOptions = trinketItems
            .filter((item) => !chosenIdsElsewhere.includes(item.id))
            .map((item) => ({
              value: item.id.toString(),
              sortLabel: `${item.name} ${item.encounter_name}`,
              className: `text-quality-${item.quality}`,
              label: item.name,
              customOptionFn: (isSelected: boolean) => (
                <div className='flex items-center gap-2 w-full min-w-0'>
                  <a
                    href={wowheadUrl(gameVersion, undefined, item.id)}
                    target='_blank'
                    rel='noreferrer'
                    onClick={(event) => event.stopPropagation()}
                    className='shrink-0'
                  >
                    <ItemIcon id={item.id} className='w-6 h-6' />
                  </a>
                  <div className='grow min-w-0'>
                    <div
                      className={cn('truncate', `text-quality-${item.quality}`)}
                    >
                      {item.name}
                    </div>
                    <div className='text-xs text-muted-foreground truncate'>
                      {item.encounter_name}
                    </div>
                  </div>
                  <Check
                    size={16}
                    className={cn(
                      'ml-auto min-w-4',
                      isSelected ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </div>
              ),
            }))

          const options = choice
            ? [
                {
                  value: TRINKET_CLEAR_VALUE,
                  sortLabel: '!',
                  label: 'No trinket',
                  className: 'text-muted-foreground',
                  customOptionFn: (isSelected: boolean) => (
                    <div className='flex items-center gap-2 w-full min-w-0'>
                      <span className='truncate text-muted-foreground'>
                        No trinket
                      </span>
                      <Check
                        size={16}
                        className={cn(
                          'ml-auto min-w-4',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </div>
                  ),
                },
                ...trinketOptions,
              ]
            : trinketOptions

          const obtainedTooltip =
            autoObtained && choice
              ? `Found equipped, on the ${detectedTrinketTrack(
                  choice.item_id,
                  trinketCache,
                )} track`
              : effectiveObtained
              ? 'Obtained'
              : 'Not obtained'

          return (
            <TrinketChoiceCard
              key={rank}
              header={trinketRankColumnLabels[rank]}
              choice={choice ?? null}
              item={chosenItem}
              options={options}
              canEdit={canEdit}
              disabled={mutation.isPending}
              autoObtained={autoObtained}
              effectiveObtained={effectiveObtained}
              obtainedTooltip={obtainedTooltip}
              onChange={(next) => setRankChoice(rank, next)}
            />
          )
        })}
      </div>
    </div>
  )
}

export default TrinketPriority
